import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Refresh as RefreshIcon, GetApp as GetAppIcon } from '@mui/icons-material';
import { apiUrl } from '../config/api';

const API_URL = apiUrl('daily_summary');

const DailySummaryManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Date management.
  const currentDateStr = new Date().toISOString().split('T')[0];
  const [summaryDate, setSummaryDate] = useState(currentDateStr);

  // New Manual Sales input (must appear first).
  const [manualSales, setManualSales] = useState('');

  // Payment Summary inputs.
  const [cardPayments, setCardPayments] = useState('');
  const [onlineSales, setOnlineSales] = useState('');
  const [creditSales, setCreditSales] = useState('');
  const [cashPayments, setCashPayments] = useState('');

  // Extra Earnings inputs.
  const [tips, setTips] = useState('');
  const [serviceCharges, setServiceCharges] = useState('');

  // Cashier inputs.
  const [openingBalance, setOpeningBalance] = useState('');

  // Computed totals.
  // System Subtotal is the sum of the Payment Summary and Extra Earnings.
  const systemSubtotal = 
    (parseFloat(cardPayments) || 0) +
    (parseFloat(onlineSales) || 0) +
    (parseFloat(creditSales) || 0) +
    (parseFloat(cashPayments) || 0) +
    (parseFloat(tips) || 0) +
    (parseFloat(serviceCharges) || 0);

  // Difference between manually counted sales and system computed subtotal.
  const difference = (parseFloat(manualSales) || 0) - systemSubtotal;

  // Cashier balance: Opening Balance + Manual Sales.
  const cashierBalance = (parseFloat(openingBalance) || 0) + (parseFloat(manualSales) || 0);

  // State to hold loaded summary (if exists) and edit mode flag.
  const [loadedSummary, setLoadedSummary] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // State for mobile detail modal.
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Function to load summary data from the backend.
  const fetchSummary = () => {
    const url = `${API_URL}?summary_date=${summaryDate}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.summary_date) {
          setLoadedSummary(data);
          setIsEditMode(true);
          // Populate fields from the existing summary.
          setManualSales(data.manual_sales);
          setCardPayments(data.total_card_payments);
          setOnlineSales(data.total_online_sales);
          setCreditSales(data.total_credit_sales);
          setCashPayments(data.total_cash_payments);
          setTips(data.total_tips);
          setServiceCharges(data.total_service_charges);
          setOpeningBalance(data.opening_balance);
        } else {
          setLoadedSummary(null);
          setIsEditMode(false);
          setManualSales('');
          setCardPayments('');
          setOnlineSales('');
          setCreditSales('');
          setCashPayments('');
          setTips('');
          setServiceCharges('');
          setOpeningBalance('');
        }
      })
      .catch((err) => console.error("Error fetching summary", err));
  };

  // Save summary (add/update) by sending the complete payload.
  const handleSaveSummary = () => {
    const payload = {
      summary_date: summaryDate,
      total_card_payments: parseFloat(cardPayments) || 0,
      total_online_sales: parseFloat(onlineSales) || 0,
      total_credit_sales: parseFloat(creditSales) || 0,
      total_cash_payments: parseFloat(cashPayments) || 0,
      total_tips: parseFloat(tips) || 0,
      total_service_charges: parseFloat(serviceCharges) || 0,
      manual_sales: parseFloat(manualSales) || 0,
      opening_balance: parseFloat(openingBalance) || 0,
      subtotal: systemSubtotal,
      difference: difference,
      cashier_balance: cashierBalance,
      action: isEditMode ? 'update' : 'add'
    };

    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Save result:", data);
        fetchSummary();
      })
      .catch((err) => console.error("Error saving summary", err));
  };

  // Download CSV paysheet.
  const handleDownloadPaysheet = () => {
    const headers = [
      "Summary Date", 
      "Manual Total Sales", 
      "Total Card Payments", 
      "Total Online Sales", 
      "Total Credit Sales", 
      "Total Cash Payments", 
      "Total Tips", 
      "Total Service Charges", 
      "Opening Balance", 
      "System Subtotal", 
      "Difference", 
      "Cashier Balance"
    ];
    const row = [
      summaryDate,
      manualSales,
      cardPayments,
      onlineSales,
      creditSales,
      cashPayments,
      tips,
      serviceCharges,
      openingBalance,
      systemSubtotal.toFixed(2),
      difference.toFixed(2),
      cashierBalance.toFixed(2)
    ];
    const csvContent = headers.join(",") + "\n" + row.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `daily_summary_${summaryDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchSummary();
  }, [summaryDate]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Daily Cashier Checking
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          label="Summary Date"
          type="date"
          value={summaryDate}
          onChange={(e) => setSummaryDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="contained" onClick={fetchSummary} startIcon={<RefreshIcon />}>
          Load Summary
        </Button>
        {loadedSummary && (
          <Button variant="outlined" onClick={handleDownloadPaysheet} startIcon={<GetAppIcon />}>
            Download Paysheet
          </Button>
        )}
      </Box>
      
      {/* --- Manual Sales Input Block (MUST COME FIRST) --- */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Manual Total Sales
        </Typography>
        <TextField
          label="Manual Total Sales"
          type="number"
          value={manualSales}
          onChange={(e) => setManualSales(e.target.value)}
          fullWidth
          variant="standard"
        />
      </Paper>

      {/* --- Payment Summary Block --- */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Payment Summary
        </Typography>
        <TextField
          label="Total Card Payments"
          type="number"
          value={cardPayments}
          onChange={(e) => setCardPayments(e.target.value)}
          fullWidth
          variant="standard"
          sx={{ mb: 1 }}
        />
        <TextField
          label="Total Online Sales"
          type="number"
          value={onlineSales}
          onChange={(e) => setOnlineSales(e.target.value)}
          fullWidth
          variant="standard"
          sx={{ mb: 1 }}
        />
        <TextField
          label="Total Credit Sales"
          type="number"
          value={creditSales}
          onChange={(e) => setCreditSales(e.target.value)}
          fullWidth
          variant="standard"
          sx={{ mb: 1 }}
        />
        <TextField
          label="Total Cash Payments"
          type="number"
          value={cashPayments}
          onChange={(e) => setCashPayments(e.target.value)}
          fullWidth
          variant="standard"
        />
      </Paper>

      {/* --- Extra Earnings Block --- */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Extra Earnings
        </Typography>
        <TextField
          label="Total Tips"
          type="number"
          value={tips}
          onChange={(e) => setTips(e.target.value)}
          fullWidth
          variant="standard"
          sx={{ mb: 1 }}
        />
        <TextField
          label="Total Service Charges"
          type="number"
          value={serviceCharges}
          onChange={(e) => setServiceCharges(e.target.value)}
          fullWidth
          variant="standard"
        />
      </Paper>

      {/* --- Cashier Information Block --- */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Cashier Information
        </Typography>
        <TextField
          label="Opening Balance"
          type="number"
          value={openingBalance}
          onChange={(e) => setOpeningBalance(e.target.value)}
          fullWidth
          variant="standard"
          sx={{ mb: 1 }}
        />
        <Typography variant="h5">
          Cash Balance: {cashierBalance.toFixed(2)}
        </Typography>
      </Paper>

      {/* --- Computed Totals Block --- */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          System Subtotal (Payment Summary + Extra Earnings)
        </Typography>
        <Typography variant="h5">
          {systemSubtotal.toFixed(2)}
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Difference (Manual Sales - System Subtotal)
        </Typography>
        <Typography variant="h5" color={difference >= 0 ? 'green' : 'red'}>
          {difference.toFixed(2)}
        </Typography>
      </Paper>

      <Button variant="contained" onClick={handleSaveSummary}>
        {isEditMode ? "Update Summary" : "Add Summary"}
      </Button>

      {/* --- Desktop Summary Details --- */}
      {loadedSummary && !isMobile && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Summary Details for {loadedSummary.summary_date}
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Manual Total Sales</TableCell>
                <TableCell>Total Card Payments</TableCell>
                <TableCell>Total Online Sales</TableCell>
                <TableCell>Total Credit Sales</TableCell>
                <TableCell>Total Cash Payments</TableCell>
                <TableCell>Total Tips</TableCell>
                <TableCell>Total Service Charges</TableCell>
                <TableCell>Opening Balance</TableCell>
                <TableCell>System Subtotal</TableCell>
                <TableCell>Difference</TableCell>
                <TableCell>Cash Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{loadedSummary.manual_sales}</TableCell>
                <TableCell>{loadedSummary.total_card_payments}</TableCell>
                <TableCell>{loadedSummary.total_online_sales}</TableCell>
                <TableCell>{loadedSummary.total_credit_sales}</TableCell>
                <TableCell>{loadedSummary.total_cash_payments}</TableCell>
                <TableCell>{loadedSummary.total_tips}</TableCell>
                <TableCell>{loadedSummary.total_service_charges}</TableCell>
                <TableCell>{loadedSummary.opening_balance}</TableCell>
                <TableCell>{parseFloat(loadedSummary.subtotal).toFixed(2)}</TableCell>
                <TableCell>{parseFloat(loadedSummary.difference).toFixed(2)}</TableCell>
                <TableCell>{parseFloat(loadedSummary.cash_balance).toFixed(2)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* --- Mobile Detail Modal --- */}
      {isMobile && loadedSummary && (
        <Dialog
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          fullWidth
        >
          <DialogTitle>Summary Detail for {loadedSummary.summary_date}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">
              <strong>Manual Total Sales:</strong> {loadedSummary.manual_sales}
            </Typography>
            <Typography variant="body2">
              <strong>Total Card Payments:</strong> {loadedSummary.total_card_payments}
            </Typography>
            <Typography variant="body2">
              <strong>Total Online Sales:</strong> {loadedSummary.total_online_sales}
            </Typography>
            <Typography variant="body2">
              <strong>Total Credit Sales:</strong> {loadedSummary.total_credit_sales}
            </Typography>
            <Typography variant="body2">
              <strong>Total Cash Payments:</strong> {loadedSummary.total_cash_payments}
            </Typography>
            <Typography variant="body2">
              <strong>Total Tips:</strong> {loadedSummary.total_tips}
            </Typography>
            <Typography variant="body2">
              <strong>Total Service Charges:</strong> {loadedSummary.total_service_charges}
            </Typography>
            <Typography variant="body2">
              <strong>Opening Balance:</strong> {loadedSummary.opening_balance}
            </Typography>
            <Typography variant="body2">
              <strong>System Subtotal:</strong> {parseFloat(loadedSummary.subtotal).toFixed(2)}
            </Typography>
            <Typography variant="body2">
              <strong>Difference:</strong> {parseFloat(loadedSummary.difference).toFixed(2)}
            </Typography>
            <Typography variant="body2">
              <strong>Cash Balance:</strong> {parseFloat(loadedSummary.cash_balance).toFixed(2)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default DailySummaryManagement;
